import useQueryParams from 'hooks/useQueryParams';
import { Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { Question } from 'router/subrouters/DataRoom/models/QAndA';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { CreateFaqRef } from '../../components/CreateFaqDrawer/CreateFaqDrawer.component';
import FaqContext from './Faq.context';
import FloatingButton from 'components/FloatingButton';
import useCallbackRef from 'hooks/useCallbackRef';
import AskQuestionIcon from 'icons/AskQuestion.icon';
import CreateFaqDrawer from '../../components/CreateFaqDrawer';
import { FIRST_PAGE } from 'data/global';

export const FAQ_PAGE_LIMIT = 10;

const FaqProvider: React.FC = (props) => {
  const { children } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);

  const [drawer, drawerRef] = useCallbackRef<CreateFaqRef>(null);

  const {
    canUserAddFAQ,
    dataRoom: { _id: dataRoomId },
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const {
    params: { page, sort, newFaq },
    setQueryParam,
  } = useQueryParams();

  const openFAQ = useCallback(() => {
    if (canUserAddFAQ) drawer?.setOpen?.(true);
  }, [canUserAddFAQ, drawer]);

  const openForEdit = useCallback(
    (question: Question) => {
      if (canUserAddFAQ) {
        drawer?.setOpen(true);
        drawer?.openForEdit(question);
      }
    },
    [canUserAddFAQ, drawer],
  );

  const updateQuestion = useCallback(
    (question: Partial<Question>) =>
      setDisplayedQuestions((old) =>
        old.map((el) =>
          el._id === question._id ? { ...el, ...question } : el,
        ),
      ),
    [],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      try {
        const {
          data: { items, totalPages },
        } = await dataRoomApi.faqs.getFaqs(dataRoomId, {
          $page: page,
          $limit: `${FAQ_PAGE_LIMIT}`,
          ...(sort ? { $sort: sort } : {}),
        });

        setTotalPages(totalPages);
        setDisplayedQuestions(items);
        if (totalPages < +page) {
          setQueryParam('page', FIRST_PAGE);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dataRoomId, page, popServerError, setQueryParam, sort]);

  useEffect(() => {
    if (!newFaq || !canUserAddFAQ) return;

    openFAQ();
  }, [canUserAddFAQ, newFaq, openFAQ]);

  return (
    <FaqContext.Provider
      value={{
        displayedQuestions,
        totalPages,
        areQuestionsLoading: isLoading,
        setDisplayedQuestions,
        openFAQ,
        openForEdit,
        setQuestionsLoading: setIsLoading,
        updateQuestion,
      }}
    >
      {isLoading ? (
        <div className="white-panel">
          <Loader overlay={false} />
        </div>
      ) : (
        children
      )}

      {canUserAddFAQ && (
        <>
          <FloatingButton icon={AskQuestionIcon} onClick={openFAQ} />
          <CreateFaqDrawer
            ref={drawerRef}
            setDisplayedQuestions={setDisplayedQuestions}
            setQuestionsLoading={setIsLoading}
            updateQuestion={updateQuestion}
          />
        </>
      )}
    </FaqContext.Provider>
  );
};

export default FaqProvider;
